import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Scale } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { CARD_CATEGORIES, getCardCategory } from "@/lib/cardCategories";

export default function CardCategoryPage() {
  const { category } = useParams();
  const cat = getCardCategory(category);

  useSEO({
    title: cat ? `${cat.name} — Facilitated via Partner Banks | Loan Salaah` : "Credit Cards | Loan Salaah",
    description: cat ? `${cat.short} Loan Salaah facilitates ${cat.name.toLowerCase()} from partner banks — compare and apply with guidance.` : "",
  });

  if (!cat) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-40 text-center" data-testid="card-category-not-found">
        <h1 className="font-serif text-4xl font-semibold text-concordia">This card category doesn't exist.</h1>
        <Link to="/loans/credit-cards" className="mt-6 inline-block rounded-full bg-concordia px-7 py-3 text-sm font-semibold text-ivory">
          Back to Credit Cards
        </Link>
      </div>
    );
  }

  const others = CARD_CATEGORIES.filter((c) => c.slug !== cat.slug);

  return (
    <div data-testid={`card-category-${cat.slug}`} className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Link to="/loans/credit-cards" data-testid="card-category-back-link" className="group inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-concordia">
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              Credit Cards
            </Link>
            <h1 className="mt-6 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
              {cat.name}
            </h1>
            <p className="mt-4 font-serif text-xl italic text-slate sm:text-2xl">{cat.tagline}</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/80">{cat.description[0]}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/apply?product=credit-cards" data-testid="card-category-apply-button"
                className="group inline-flex items-center gap-2 rounded-full bg-concordia px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(20,40,75,0.35)]">
                Find my {cat.name.replace(" Credit Cards", "").toLowerCase()} card
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -right-3 -top-3 h-full w-full rounded-b-[1.5rem] rounded-t-[7rem] border border-brass/50" aria-hidden="true" />
              <img src={cat.image} alt={cat.name} data-testid="card-category-image"
                className="relative aspect-[4/3] w-full rounded-b-[1.5rem] rounded-t-[7rem] object-cover shadow-[0_30px_70px_rgba(20,40,75,0.2)]" />
            </div>
          </Reveal>
        </div>

        {/* Detail */}
        <div className="mt-20 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">Read this before you pick</h2>
            <p className="mt-6 text-base leading-relaxed text-charcoal/80">{cat.description[1]}</p>
            <div className="mt-8 rounded-3xl border border-sand bg-white p-8" data-testid="card-category-compare">
              <div className="flex items-center gap-3">
                <Scale className="h-5 w-5 text-brass" />
                <h3 className="font-serif text-2xl font-semibold text-concordia">What to compare</h3>
              </div>
              <ul className="mt-5 space-y-3">
                {cat.compare.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-3xl bg-concordia-ink p-8 text-ivory" data-testid="card-category-bestfor">
              <h3 className="font-serif text-2xl font-semibold">Who this suits</h3>
              <ul className="mt-5 space-y-3">
                {cat.bestFor.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-ivory/75">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-brass" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-ivory/10 pt-5 text-xs leading-relaxed text-ivory/50">
                Loan Salaah facilitates cards from partner banks — we never issue cards, and we never pull or
                calculate your credit score. The issuing bank makes every approval decision.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Other categories */}
        <Reveal className="mt-20">
          <h3 className="font-serif text-2xl font-semibold text-concordia">Other card categories</h3>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="card-category-related">
            {others.map((c) => (
              <Link key={c.slug} to={`/loans/credit-cards/${c.slug}`} data-testid={`card-category-link-${c.slug}`}
                className="group inline-flex items-center gap-2 rounded-full border border-sand bg-white px-5 py-2.5 text-sm font-medium text-charcoal/80 transition-all duration-300 hover:border-concordia hover:text-concordia">
                {c.name.replace(" Credit Cards", "")}
                <ArrowUpRight className="h-3.5 w-3.5 text-brass transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
